// A resize boundary's pointer and keyboard behavior. A pointer drag previews its width on every move with
// no transition and commits once on release; each arrow key press commits. Widths clamp to the column's
// bounds and its content floor, and a double click fits the column to its rendered content, committed like
// any other resize (Reset column widths in View options restores the configured initial widths).

import { useCallback, useEffect, useRef, useState } from 'react'

import { RESIZE_STEP, RESIZE_STEP_LARGE, clampWidth } from './view'

export const RESIZING_CLASS = 'me-table-col-resizing'

const stop = event => {
  event.preventDefault()
  event.stopPropagation()
}

/**
 * @param {object} options
 * @param {import('../types').NormalizedColumn} options.column
 * @param {number} options.width the rendered width
 * @param {number} [options.floor] the column's content floor; a drag or key press stops there
 * @param {(width: number) => void} options.commit
 * @param {() => void} options.onPreview the table re-measures its overflow geometry
 * @param {() => ?number} [options.fit] the column's fit width, measured on demand (contentFloor.js fitWidth)
 * @returns {{live: ?number, handlers: object}} live is the dragged width while a drag runs
 */
export const useResize = ({ column, width, floor, commit, onPreview, fit }) => {
  const [live, setLive] = useState(null)
  const drag = useRef(null)

  const finish = useCallback(
    (event, keep) => {
      const current = drag.current
      if (!current) return
      drag.current = null
      document.body.classList.remove(RESIZING_CLASS)
      const target = event && event.currentTarget
      if (
        target &&
        typeof target.releasePointerCapture === 'function' &&
        typeof target.hasPointerCapture === 'function' &&
        target.hasPointerCapture(current.pointerId)
      )
        target.releasePointerCapture(current.pointerId)
      setLive(null)
      if (keep && current.last !== current.start) commit(current.last)
    },
    [commit]
  )

  useEffect(
    () => () => {
      if (drag.current) document.body.classList.remove(RESIZING_CLASS)
    },
    []
  )

  const handlers = {
    onPointerDown: event => {
      if (event.button !== 0) return
      stop(event)
      const target = event.currentTarget
      if (typeof target.setPointerCapture === 'function')
        target.setPointerCapture(event.pointerId)
      if (typeof target.focus === 'function') target.focus()
      drag.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        start: width,
        last: width,
      }
      document.body.classList.add(RESIZING_CLASS)
    },
    onPointerMove: event => {
      const current = drag.current
      if (!current) return
      const next = clampWidth(
        column,
        current.start + (event.clientX - current.startX),
        floor
      )
      if (next === null || next === current.last) return
      current.last = next
      setLive(next)
      onPreview()
    },
    onPointerUp: event => finish(event, true),
    onLostPointerCapture: event => finish(event, true),
    onPointerCancel: event => {
      finish(event, false)
      onPreview()
    },
    onKeyDown: event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      if (event.altKey || event.ctrlKey || event.metaKey) return
      stop(event)
      const step = event.shiftKey ? RESIZE_STEP_LARGE : RESIZE_STEP
      const next = clampWidth(
        column,
        width + (event.key === 'ArrowRight' ? step : -step),
        floor
      )
      if (next !== null && next !== width) commit(next)
    },
    onDoubleClick: event => {
      stop(event)
      const fitted = typeof fit === 'function' ? fit() : null
      const next = clampWidth(column, fitted, floor)
      if (next !== null && next !== width) commit(next)
    },
    // the boundary sits inside a sortable header; its clicks never sort
    onClick: event => event.stopPropagation(),
  }

  return { live, handlers }
}
